import { Component, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ConfigurationProfile } from '../../../../viewmodels/apple/configuration-profile.model';

@Component({
  selector: 'ml-mdm-configuration-profile',
  templateUrl: './configuration-profile.component.html',
  styleUrls: ['./configuration-profile.component.scss'],
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class ConfigurationProfileComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly configurationProfile = input.required<ConfigurationProfile>();
  readonly mutate = input(false);
  readonly allowToggle = input(true);
  readonly configurationProfileChange = output<ConfigurationProfile>();

  editing = false;

  public configurationProfileForm: UntypedFormGroup;

  constructor() {
    this.createForm();

    effect(() => {
      const configurationProfile = this.configurationProfile();
      this.rebuildForm(configurationProfile);
    });
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    if (this.configurationProfileForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.configurationProfileChange.emit(result);
    return true;
  }

  toggleEditing() {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }

  private createForm() {
    this.configurationProfileForm = this.formBuilder.group({
      hasRemovalPasscode: false,
      isEncrypted: false,
      isManaged: true,
      payloadDescription: '',
      payloadDisplayName: '',
      payloadExpirationDate: Date,
      payloadIdentifier: '',
      payloadOrganization: '',
      payloadUUID: '',
      payloadRemovalDisallowed: false,
      removalDate: Date,
      durationUntilRemoval: 0,
    });
  }

  private rebuildForm(configurationProfile: ConfigurationProfile) {
    this.configurationProfileForm.reset({
      hasRemovalPasscode: configurationProfile.hasRemovalPasscode,
      isEncrypted: configurationProfile.isEncrypted,
      isManaged: configurationProfile.isManaged,
      payloadDescription: configurationProfile.payloadDescription,
      payloadDisplayName: configurationProfile.payloadDisplayName,
      payloadExpirationDate: configurationProfile.payloadExpirationDate,
      payloadIdentifier: configurationProfile.payloadIdentifier,
      payloadOrganization: configurationProfile.payloadOrganization,
      payloadUUID: configurationProfile.payloadUUID,
      payloadRemovalDisallowed: configurationProfile.payloadRemovalDisallowed,
      removalDate: configurationProfile.removalDate,
      durationUntilRemoval: configurationProfile.durationUntilRemoval,
    });
  }

  private populate(): ConfigurationProfile {
    let configurationProfile = this.configurationProfile();
    if (!this.mutate()) {
      configurationProfile = new ConfigurationProfile();
    }

    configurationProfile.hasRemovalPasscode = this.configurationProfileForm.get('hasRemovalPasscode')?.value;
    configurationProfile.isEncrypted = this.configurationProfileForm.get('isEncrypted')?.value;
    configurationProfile.isManaged = this.configurationProfileForm.get('isManaged')?.value;
    configurationProfile.payloadDescription = this.configurationProfileForm.get('payloadDescription')?.value;
    configurationProfile.payloadDisplayName = this.configurationProfileForm.get('payloadDisplayName')?.value;
    configurationProfile.payloadExpirationDate = this.configurationProfileForm.get('payloadExpirationDate')?.value;
    configurationProfile.payloadIdentifier = this.configurationProfileForm.get('payloadIdentifier')?.value;
    configurationProfile.payloadOrganization = this.configurationProfileForm.get('payloadOrganization')?.value;
    configurationProfile.payloadUUID = this.configurationProfileForm.get('payloadUUID')?.value;
    configurationProfile.payloadRemovalDisallowed =
      this.configurationProfileForm.get('payloadRemovalDisallowed')?.value;
    configurationProfile.durationUntilRemoval = this.configurationProfileForm.get('durationUntilRemoval')?.value;

    return configurationProfile;
  }
}
